import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next'; // Import the translation hook
import Breadcrumb from '@/components/Breadcrumb';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import DefaultLayout from '@/layout/DefaultLayout';
import {
  DepartementResponse,
  deleteDepartement,
  getDepartementsByCompanyId,
} from '@/services/DepartementService';

function Departements() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<string>('');
  const navigate = useNavigate();

  const { t } = useTranslation(); // Use the default namespace

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('Departements.deleteButton')} {/* Translate the delete button */}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('Departements.editButton')} {/* Translate the edit button */}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteDepartement.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('Departements.titleColumn'), // Translate the title column header
        accessor: 'title',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('Departements.branchColumn'), // Translate the branch column header
        accessor: 'branchTitle',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('Departements.actionsColumn'), // Translate the actions column header
        accessor: 'id', // Assuming you have an 'id' field in your data to identify rows
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteDepartement}
          />
        ),
      },
    ],
    [t] // Add the t function as a dependency
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: departements,
  } = useQuery<DepartementResponse | undefined>(
    ['departments', pageIndex, pageSize, search],
    () =>
      getDepartementsByCompanyId({
        fromvalue: pageIndex,
        takevalue: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeleteDepartement = useMutation(
    async (id: string) => {
      await deleteDepartement(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'departments',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((departements?.count ?? 0) / pageSize);
  }, [pageSize, departements]);
  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('Departements.pageName')}
        addbtn={true}
        addRouter="new"
      />{' '}
      {/* Translate the breadcrumb */}
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={departements?.count}
            data={departements?.departments ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default Departements;
